import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Stethoscope, Activity, HeartPulse, ShieldCheck, ArrowRight,
  LogOut, ChevronDown, Lock, Gauge, Headset,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { HOME } from "@/App";

const PORTALS = [
  { key: "doctor", to: "/doctor/login", name: "Doctor", desc: "Order treatments & track outcomes", icon: Stethoscope, accent: "#1A3A6D" },
  { key: "pro", to: "/pro/login", name: "Clinician", desc: "View assigned cases & log therapy sessions", icon: Activity, accent: "#2A5298" },
  { key: "patient", to: "/patient/login", name: "Patient", desc: "Confirm sessions & track recovery", icon: HeartPulse, accent: "#1A3A6D" },
  { key: "admin", to: "/admin/login", name: "Admin", desc: "Platform config & financial management", icon: ShieldCheck, accent: "#0A1B33" },
  { key: "ops", to: "/ops/login", name: "Operations", desc: "Assignment, clinician workforce & vendor billing", icon: Headset, accent: "#0A1B33" },
];

const HERO_IMG = "/praniq-hero.jpeg";

function PortalsDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="nav-portals-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full bg-navy-700 px-5 py-2.5 text-[13.5px] font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-navy-800"
      >
        Sign in
        <ChevronDown size={15} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="nav-portals-dropdown-menu"
          className="absolute right-0 mt-2 w-[300px] origin-top-right rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_24px_60px_rgba(10,27,51,.22)] animate-fade-up z-50"
        >
          {PORTALS.map((p) => (
            <Link
              key={p.key}
              to={p.to}
              data-testid={`nav-portal-link-${p.key === "pro" ? "clinician" : p.key}`}
              onClick={() => setOpen(false)}
              className="group flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors duration-200 hover:bg-slate-50"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white" style={{ background: p.accent }}>
                <p.icon size={17} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-bold text-slate-900">{p.name}</span>
                <span className="block truncate text-[11.5px] text-slate-500">{p.desc}</span>
              </span>
              <ArrowRight size={15} className="shrink-0 text-slate-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-navy-700" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "radial-gradient(circle at 12% 12%, rgba(42,82,152,.12), transparent 42%), radial-gradient(circle at 92% 88%, rgba(218,165,32,.10), transparent 45%), linear-gradient(180deg,#f6f8fc,#eef2fa)" }}>
      {/* Sticky nav */}
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/75 backdrop-blur-xl">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-3.5">
          <Link to="/" data-testid="nav-logo"><Logo height={34} /></Link>
          <nav className="flex items-center gap-4">
            {user && (
              <Link to={HOME[user.role] || "/login"} className="hidden text-[13px] font-semibold text-navy-700 hover:text-navy-800 sm:inline" data-testid="nav-my-portal">
                My portal
              </Link>
            )}
            <PortalsDropdown />
          </nav>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col justify-center px-6 py-14">
        {user && (
          <div className="mb-10 flex flex-wrap items-center gap-3 rounded-2xl border border-gold-400/30 bg-[#0A1B33] px-5 py-4 text-white shadow-lg animate-fade-in" data-testid="landing-session-banner">
            <span className="text-[13.5px] text-white/80">Signed in as <b className="text-white">{user.name}</b> · <span className="capitalize">{user.role === "physiotherapist" ? "clinician" : user.role}</span></span>
            <div className="ml-auto flex gap-2">
              <Link to={HOME[user.role] || "/login"} className="pq-btn pq-btn-sm border-gold-500 bg-gold-500 font-semibold text-[#0A1B33] hover:bg-gold-400" data-testid="landing-continue-btn">Go to my portal <ArrowRight size={14} /></Link>
              <button onClick={logout} className="pq-btn pq-btn-sm border-white/25 text-white/85 hover:bg-white/10 hover:text-white" data-testid="landing-logout-btn"><LogOut size={14} /> Sign out</button>
            </div>
          </div>
        )}

        {/* Hero */}
        <section className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1.15fr_1fr]">
          <div className="animate-fade-up">
            <div className="pq-badge mb-6 bg-gold-50 text-gold-600 ring-1 ring-gold-400/30">Managed physiotherapy rehabilitation platform</div>
            <h1 className="font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              One platform.<br /><span className="text-navy-700">Every step of recovery.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-500">
              PranIQ connects doctors, clinicians, patients and care administrators on one secure platform — from treatment order to full recovery. Sign in above to continue.
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {[
                { icon: ShieldCheck, label: "NMC 6.4.1 compliant" },
                { icon: Lock, label: "Encrypted care records" },
                { icon: Gauge, label: "Real-time outcomes" },
              ].map((m) => (
                <span key={m.label} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/70 px-3.5 py-1.5 text-[12.5px] font-medium text-slate-600">
                  <m.icon size={14} className="text-navy-700" /> {m.label}
                </span>
              ))}
            </div>
          </div>
          <div className="relative animate-fade-in">
            <div className="overflow-hidden rounded-3xl border border-white/60 shadow-[0_30px_70px_rgba(10,27,51,.22)]">
              <img src={HERO_IMG} alt="Clinician guiding a patient through rehabilitation" className="h-[360px] w-full object-cover" />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200/70">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-center px-6 py-6">
          <p className="text-[12.5px] text-slate-400">© 2026 PranIQ Health · NMC Reg 6.4.1 compliant</p>
        </div>
      </footer>
    </div>
  );
}
